import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { 
  TaxComponentAccount, 
  TaxComponentAccountCreate, 
  TaxComponentAccountUpdate, 
  AccountResponse,
  TaxComponent
} from '@/app/types';

// Zod Schema for Tax Component Account Form
const formSchema = z.object({
  tax_component_id: z.number({ required_error: "Tax component is required" }),
  account_id: z.number({ required_error: "Account is required" }),
});

type FormValues = z.infer<typeof formSchema>;

interface TaxComponentAccountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TaxComponentAccountCreate | TaxComponentAccountUpdate) => void;
  defaultValues?: Partial<TaxComponentAccount>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  accounts: AccountResponse[];
  taxComponents: TaxComponent[];
}

const TaxComponentAccountForm: React.FC<TaxComponentAccountFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  accounts,
  taxComponents,
}) => {
  // Initialize the form with defaultValues or empty values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tax_component_id: defaultValues?.tax_component_id || 0,
      account_id: defaultValues?.account_id || 0,
    },
  });

  const handleSubmit: SubmitHandler<FormValues> = (values: FormValues): void => {
    onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Tax Component Account Mapping' : 'Edit Tax Component Account Mapping'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-2">
            <FormField
              control={form.control}
              name="tax_component_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax Component</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value ? field.value.toString() : undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Tax Component" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {taxComponents.map((component) => (
                        <SelectItem key={component.id} value={component.id.toString()}>
                          {component.name} ({component.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="account_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    defaultValue={field.value ? field.value.toString() : undefined}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts
                        .filter(account => account.is_active) // Only show active accounts
                        .map((account) => (
                          <SelectItem key={account.id} value={account.id.toString()}>
                            {account.name} ({account.code})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TaxComponentAccountForm; 